import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AccountState } from 'src/app/store/account/account.state';
import { StateManager } from 'src/app/store/state-manager.service';

/**
 * Partner Admin Guard
 *
 * Authorizes and reconciles partner-scoped admin routes
 * (`/admin/:partnerId/...`). Makes the URL the source of truth for which
 * partner the admin portal renders, so a copied/deep-linked admin URL
 * always resolves to a specific partner rather than whatever happened to
 * be active.
 *
 * Two responsibilities:
 *
 * 1. **Authorize.** The user must have an `Administrator` profile in the
 *    `:partnerId` partner. Missing membership or a non-admin role is
 *    redirected to `/my-blume`. Comparison is strict against the canonical
 *    `'Administrator'` value (see `User.role`) — bad data fails rather than
 *    being coerced.
 *
 * 2. **Reconcile.** If the URL's partner differs from the currently-loaded
 *    one, switch the client to the URL's partner: reset team-scoped state
 *    (so `StateManager` re-initializes) and re-run the standard
 *    `/loading` flow, which reinitializes every team-scoped store for the
 *    new partner before continuing on to the requested admin URL.
 *
 * Should be composed AFTER `authGuard`, `emailVerifiedGuard`, and
 * `profileGuard` so the account, partners, and profiles are loaded by the
 * time this runs. Replaces `adminGuard` on partner-scoped admin routes.
 */
export const partnerAdminGuard: CanActivateFn = async (route, state) => {
  const accountState = inject(AccountState);
  const stateManager = inject(StateManager);
  const router = inject(Router);

  const partnerId = route.paramMap.get('partnerId');

  // No partner in the URL — nothing to authorize against.
  if (!partnerId) {
    return router.createUrlTree(['/my-blume']);
  }

  // Authorize: the user must be an Administrator in the URL's partner, OR be
  // Blume staff (super_admin / developer / revenue) entering via staff access,
  // which grants Administrator-equivalent access to any district.
  //
  // Membership is resolved against the member doc first — the profiles map is
  // derived from the global account doc, which lags until an invite is
  // accepted. See AccountState.ensureMembershipLoaded.
  await accountState.ensureMembershipLoaded(partnerId);
  const profile = accountState.profiles().get(partnerId);
  const isAdminMember = profile?.role === 'Administrator';
  const isStaffEntry = !isAdminMember && accountState.isDistrictStaff();
  if (!isAdminMember && !isStaffEntry) {
    return router.createUrlTree(['/my-blume']);
  }

  // Reconcile: if the loaded partner differs from the URL's partner, switch
  // to it via the standard loading flow so all team-scoped stores reinit.
  // Staff entering a non-member district go through the staff-access path.
  if (accountState.activeProfileId() !== partnerId) {
    await stateManager.resetTeamStates();
    if (isStaffEntry) {
      await accountState.enterDistrictAsStaff(partnerId);
    } else {
      await accountState.setActiveProfile(partnerId);
    }
    return router.createUrlTree(['/loading'], { queryParams: { returnUrl: state.url } });
  }

  return true;
};
